import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "@xstate/react";
import { useRouter } from "next/router";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { newPosOrder } from "../../core/order.core";
import { useTranslation } from "../../core/translation.core";
import useCart from "../../hooks/services/useCart.hook";
import useCheckoutTotal from "../../hooks/services/useCheckoutTotal.hook";
import { usePosGuestCustomer } from "../../hooks/services/useCustomers";
import useRetailShop from "../../hooks/useRetailShop";
import BackIcon from "../../icons/back.icon";
import ContactIcon from "../../icons/contact.icon";
import posIcons from "../../icons/pos.icons";
import { RootStateContext } from "../../states/root.state";
import { CheckoutUser } from "../../types/checkout";
import { RetailShopRes } from "../../types/retailShop";
import { PosModal } from "../reuseables/modal.comp";
import { FormData, convertCartToFormData } from "./cartFunctions";
import { CartFooter } from "./cartPanel/CartFooter";
import CartProduct from "./cartPanel/CartProduct";
import AddNewCustomer from "./customerPanel/AddNewCustomer";
import ChooseOption from "./customerPanel/ChooseOption";
import FindCustomer from "./customerPanel/FindCustomer";
import ScanMember from "./customerPanel/ScanMember";
import PauseIcon from "../../icons/pause.icon";
import UserIcon from "../../icons/user.icon";

export const linkStyle =
  "flex items-center gap-2 bg-[#EEEEEE] dark:bg-her-backblue px-3 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed";

export default function CartPanel() {
  const router = useRouter();
  const methods = useForm<FormData>();
  const { t, msg } = useTranslation();
  const { cart, removeItem, updateItem, removeAllProducts, updateCartStatusToHold, listHeldCarts } = useCart();
  const { checkoutService } = useContext(RootStateContext);
  const context = useSelector(checkoutService, (state) => state.context);
  const { checkoutTotal } = useCheckoutTotal({
    // @ts-ignore
    cart,
    userId: context.customer?.id,
  });

  const shop = useRetailShop();
  const { data: guestUser } = usePosGuestCustomer();

  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [showScanMember, setShowScanMember] = useState(false);
  const [showFindCustomer, setShowFindCustomer] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showChooseOption, setShowChooseOption] = useState(true);

  const modelBtnRef = useRef(null);

  function handleCartSwitch() {
    router.push(`?mode=heldCart&cartOpen=true`);
  }

  const handleCustomerModal = () => {
    setShowModal(!showModal);
  };

  // useEffect(() => {
  //   removeAllCoupons();
  // }, [context.customer?.id]);

  useEffect(() => {
    if (cart) {
      const formData = convertCartToFormData(cart);

      methods.reset(formData);
    }
  }, [cart, methods]);

  async function checkoutFn() {
    const formData = methods.getValues();

    if (formData.items.length < 1) {
      return toast.error("Please add items to cart");
    }

    // note that checkout session is created on component init (in useCheckoutTotal) and is constantly updated with cart changes
    // we invalidate just to ensure already created section is in sync.
    queryClient.invalidateQueries({ queryKey: ["posCheckout"] });

    if (context?.customer) {
      const order = await newPosOrder({
        orderNote: context.orderNote,
        userId: context.customer.id,
      });

      queryClient.invalidateQueries({ queryKey: ["PosCart", router.locale] });
      queryClient.invalidateQueries({ queryKey: ["PosCartCount"] });
      queryClient.invalidateQueries({ queryKey: ["PosCartTotalPrice"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });

      checkoutService.send({
        remainingBalance: checkoutTotal?.total,
        type: "ASSIGN_BALANCE",
      });

      router.push(`/new-order/checkout?orderId=${order.id}`);
    } else {
      toast.error(t(msg.notifications.selectCustomerFirst));
    }
  }

  async function onlineCheckoutFn() {
    try {
      const formData = methods.getValues();

      if (formData.items.length < 1) {
        return toast.error("Please add items to cart");
      }

      router.push(`/new-order/online-order?userId=${context.customer.id}`);
    } catch (error) {
      console.error(error);
    }
  }

  async function handleHoldCart() {
    // @ts-ignore
    if (cart.items.length < 1) {
      return toast.error(t(msg.cart.pleaseAddProductToCart));
    } else if (!context?.customer?.id) {
      return toast.error(t(msg.cart.selectCustomerFirst));
    } else {
      await updateCartStatusToHold.mutate({
        customerId: context?.customer?.id,
      });
    }
  }

  useEffect(() => {
    if (updateCartStatusToHold.error) {
      toast.error(updateCartStatusToHold.error.toString());
      updateCartStatusToHold.reset();
    }

    if (updateCartStatusToHold.isSuccess) {
      updateCartStatusToHold.reset();

      checkoutService.send({
        type: "RESET_XSTATE",
      });

      router.push("/?mode=heldCart");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCartStatusToHold.error, updateCartStatusToHold.isSuccess]);

  if (typeof cart === "undefined") return null;
  if (!shop || !guestUser) return null;
  return (
    <>
      <main className="h-[91vh] relative overflow-x-hidden flex flex-col justify-between bg-white dark:bg-her-darkblue2 dark:text-white">
        <button onClick={() => router.push("/")} className="shrink-0 flex lg:hidden items-center w-full p-4 gap-2 border-b">
          <BackIcon />
          <h1 className="text-sm">{t(msg.pos.back)}</h1>
        </button>

        <section className="shrink-0 flex items-center justify-between flex-wrap px-4 pt-4 pb-2 border-b gap-4">
          <CustomerComp handleCustomerModal={handleCustomerModal} customer={context.customer} shop={shop} guestUser={guestUser} />

          <div className="flex gap-4">
            {listHeldCarts.data && listHeldCarts.data.length > 0 && (
              <button onClick={handleCartSwitch} className={linkStyle}>
                <PauseIcon className="w-5 h-5" />
                <span className="text-sm">{t(msg.pos.viewHeldCart)}</span>
              </button>
            )}

            <button disabled={cart.items.length < 1 || !context.customer?.id} onClick={handleHoldCart} className={linkStyle}>
              <PauseIcon className="w-5 h-5" />
              <span className="text-sm">{t(msg.pos.holdCart)}</span>
            </button>

            <button id="clear-cart-btn" onClick={() => removeAllProducts()}>
              {posIcons.deleteIcon}
            </button>
          </div>
        </section>

        <div className="grow overflow-y-scroll">
          {cart.items.length > 0 ? (
            <section className={`space-y-5 px-4 pt-6`}>
              {cart.items.map((i, idx) => (
                <CartProduct key={idx} item={i} removeItem={removeItem} updateItem={updateItem} />
              ))}
            </section>
          ) : (
            <div className="flex-center h-full">{posIcons.emptyCartIcon}</div>
          )}
        </div>

        <div className="shrink-0">
          <CartFooter onlineOrderFn={onlineCheckoutFn} checkoutFn={checkoutFn} cart={cart} userId={context.customer?.id} />
        </div>
      </main>
      {showModal && (
        <PosModal
          setModalOpened={setShowModal}
          // @ts-ignore
          btnRef={modelBtnRef}
          onclick={() => {
            setShowAddCustomer(false);
            setShowFindCustomer(false);
            setShowScanMember(false);
            setShowChooseOption(true);
          }}
        >
          {showScanMember && <ScanMember setChooseOption={setShowChooseOption} setShowScanMember={setShowScanMember} />}

          {showFindCustomer && (
            <FindCustomer
              setChooseOption={setShowChooseOption}
              setShowFindCustomer={setShowFindCustomer}
              setShowModal={setShowModal}
            />
          )}

          {showAddCustomer && <AddNewCustomer setChooseOption={setShowChooseOption} showAddCustomer={setShowAddCustomer} />}

          {showChooseOption && (
            <ChooseOption
              setChooseOption={setShowChooseOption}
              setAddShowCustomer={setShowAddCustomer}
              setShowFindCustomer={setShowFindCustomer}
              setShowScanMember={setShowScanMember}
            />
          )}
        </PosModal>
      )}
    </>
  );
}

interface CustomerCompProps {
  handleCustomerModal: VoidFunction;
  customer?: CheckoutUser;
  guestUser: CheckoutUser;
  shop: RetailShopRes;
}
const CustomerComp = ({ handleCustomerModal, customer, shop, guestUser }: CustomerCompProps) => {
  const { t, msg } = useTranslation();
  const { checkoutService } = useContext(RootStateContext);

  useEffect(() => {
    if (shop.autoSelectGuest && !customer) {
      handleSelectCustomer(guestUser);
    }
  }, []);

  async function handleSelectCustomer(customer: CheckoutUser) {
    checkoutService.send({
      type: "RESET_XSTATE",
    });

    checkoutService.send({
      customer: customer,
      type: "ASSIGN_CUSTOMER_INFO",
    });
  }

  return (
    <div>
      {customer?.name ? (
        <button id="customer-btn" onClick={handleCustomerModal} className={`${linkStyle} py-2`}>
          <ContactIcon className="w-6 h-6" />
          <h4 className="text-sm">{customer?.name} </h4>
        </button>
      ) : (
        <button id="add-customer-btn" onClick={handleCustomerModal} className={`${linkStyle} py-2`}>
          <UserIcon className="text-her-paradisePink w-6 h-6" />
          <span className="text-sm">{t(msg.pos.customer)} </span>
        </button>
      )}
    </div>
  );
};
